/* Copyright (c) 2021 Magnusson Institute, All Rights Reserved */

import React from "react"
import { Avatar, Button, Fade, FormControl, Grid, IconButton, InputAdornment, LinearProgress, OutlinedInput, Paper, Typography } from "@mui/material";
import WalletContext from "../contexts/WalletContext";
import { useContext } from "react";
import AuthContext from "../contexts/AuthContext";
import * as __ from 'lib384/dist/384.esm'
import { config } from "config";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { VisibilityOff, Visibility } from "@mui/icons-material";
import SwipeableViews from 'react-swipeable-views';

const snackabra = new __.Snackabra(config);


const Login = () => {
    const auth = useContext(AuthContext)
    const wallet = React.useContext(WalletContext)
    const [open, setOpen] = React.useState(false)
    const [openWalletDialog, setOpenLoadWalletDialog] = React.useState(false)
    const [controlMessages, setControlMessages] = React.useState<__.ChannelMessage[]>([])
    const [messages, setMessages] = React.useState<__.ChannelMessage[]>([])
    const [channel, setChannel] = React.useState<__.ChannelSocketTypeAlias>(undefined as unknown as __.ChannelSocketTypeAlias)
    const [openSecret, setOpenSecret] = React.useState<boolean>(false);
    const [note, setNote] = React.useState<string>('')
    const [importedWalletId, setImportedWalletId] = React.useState<string | undefined>(undefined)
    const [warning, setWarning] = React.useState<string>('')
    const [pin, setPin] = React.useState<string[]>(['', '', '', ''])
    const [phrase, setPhrase] = React.useState<string[]>(['', '', ''])
    const [loading, setLoading] = React.useState(false);
    const [success, setSuccess] = React.useState(false);
    const [error, setError] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [activeStep, setActiveStep] = React.useState(0);

    React.useEffect(() => {
        if (wallet.id) {
            const loadWallet = async () => {
                console.log('wallet loaded')

                const c = await snackabra.connect(
                    onMessage,
                    wallet.identity,
                    wallet.id as string,
                );
                setChannel(c);
            }
            loadWallet();
        }
    }, [wallet.id])

    const handleNext = async () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        try{


        if (pin.join('').trim().length !== 16) {
            setError(true)
            return
        }
        setError(false)
        const jwk = await auth.setPassPhrase(pin.join(' ').trim())
        console.log(jwk)
        const sb384 = new __.NewSB.SB384(jwk)
        await sb384.ready
        console.log(sb384)
        const _wallet = {key: jwk, channelId: sb384.hash}
        await snackabra.connect(
            onMessage,
            jwk,
            sb384.hash,
        );
        auth.setAfterAuth(JSON.stringify(_wallet))
        // console.log(walletId, pin.join(' ').trim())
    } catch(e){
        alert('That didn\'t work, let\'s try again.')
        handleBack()
        console.error(e)
    }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };


    const onMessage = async (msg: __.ChannelMessage) => {
        // let received = await window.Snackabra.channel.socket.receive(message)
        // const msg = JSON.parse(received);
        console.log(msg)
        if (msg.hasOwnProperty('verificationToken')) {
            setControlMessages([...controlMessages, msg])
        } else {
            setMessages([...messages, msg])
        }


    }

    const toggleDebugger = () => {
        setOpen(!open)
    }

    const openLoadWalletDialog = () => {
        setOpenLoadWalletDialog(true)
    }

    const protectWallet = async (password: string, walletId?: string) => {
        setNote('')
        setOpenSecret(false)
        try {
            if (!walletId) throw new Error('No wallet id provided')
            auth.setPassPhrase(password).then(() => {
                wallet.registerWallet(walletId)
            })
        } catch (e) {
            setNote('That didn\'t work, let\'s try again.')
            setOpenSecret(true)
        }

    }

    const handleButtonClick = async () => {
        if (!loading) {
            setSuccess(false);
            setError(false);
            setLoading(true);
            setImportedWalletId(pin.join(' ').trim())
            handleNext()
        }
    };

    function chunkIntoSets(str: string): string[] {
        const sets = [];
        let currentSet = "";

        for (let i = 0; i < str.length; i++) {
            currentSet += str[i];
            if (currentSet.length === 4 || i === str.length - 1) {
                sets.push(currentSet);
                currentSet = "";
            }
        }

        return sets;
    }

    const validatePin = (pin: string[]): string[] => {
        for (let x in pin) {
            if (pin[x].length === 4) {
                const processed = __.crypto.strongpin.process(pin[x])
                console.log(processed)
                if (processed !== pin[x]) {
                    pin[x] = processed
                    setWarning('We detected a typo in your pin, we fixed it for you. But, you should double check your pin.')
                }
            }
        }
        return pin
    }

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter' && pin.join('').trim().length === 16) {
            handleButtonClick()
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value.replace(/ /g, '')
        if (value.length > 16) return
        const _pin = chunkIntoSets(value)
        setPin(validatePin(_pin))
    }

    const handleChangePhrase = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value.split(' ')
        setPhrase(value)
    }

    return (
        <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            sx={{ padding: '0px 24px', height: '100vh', background: 'none' }}
        >
            <Paper sx={{ p: 3, width: 360 }}>
                <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                >
                    <Typography variant="h2" align="center">
                        384 Inc
                    </Typography>
                    <Typography variant="h5" align="center">
                        Login
                    </Typography>
                    <Grid item>
                        <Avatar
                            alt="Account Image"
                            src="/broken-image.jpg"
                            sx={{ width: 56, height: 56 }}
                        >
                            <AccountCircleIcon sx={{ fontSize: 64 }} />
                        </Avatar>
                    </Grid>
                    <Grid item>
                        <SwipeableViews style={{ padding: 0 }} index={activeStep}>
                            <FormControl sx={{ m: 1, width: '100%', ml: 0, mr: 0 }} variant="outlined">
                                <OutlinedInput
                                    id="outlined-adornment-password"
                                    type={showPassword ? 'text' : 'password'}
                                    size="small"
                                    error={error}
                                    sx={{ pr: 0 }}
                                    value={pin.join(' ').trim() !== '' ? pin.join(' ') : ''}
                                    onChange={handleChange}
                                    onKeyUp={handleKeyUp}
                                    autoFocus
                                    autoComplete="off"
                                    endAdornment={
                                        <InputAdornment
                                            position="end">
                                            <IconButton
                                                sx={{ mr: 1 }}
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                            <Button
                                                style={{ borderRadius: '0px 4px 4px 0px' }}
                                                variant={'contained'} onClick={handleNext}>Next</Button>
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                            <Grid
                                justifyContent={'center'}
                                alignItems={'center'}
                                container>
                                <Grid item>
                                    <IconButton
                                        sx={{ mr: 1 }}
                                        aria-label="go back to strongpin"
                                        onClick={handleBack}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        <ArrowBackIcon />
                                    </IconButton>
                                </Grid>
                                <Grid xs={10} item>

                                    <LinearProgress color="inherit" />

                                </Grid>
                            </Grid>

                        </SwipeableViews>
                    </Grid>

                    <Fade in={warning !== ''}>
                        <Grid item>
                            <Typography variant="subtitle2" align="center" color={'#ff7961'}>{warning}</Typography>
                        </Grid>
                    </Fade>

                </Grid>
            </Paper>

        </Grid>
    )
}

export default Login;


